import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

// 🎨 GLOBAL COLORS DEFINITION 
const colors = {
    primary: '#6366F1',
    secondary: '#8B5CF6',
    accent: '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    dark: '#1E293B',
    light: '#F8FAFC',
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.7)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    gradientMain: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    designerGradient: 'linear-gradient(135deg, #E91E63 0%, #9C27B0 100%)'
};

function DesignerTasks() {
    const [currentUser, setCurrentUser] = useState(null);
    const [designers, setDesigners] = useState([]);
    const [selectedDesigner, setSelectedDesigner] = useState('');
    const [formData, setFormData] = useState({
        videoName: '',
        rawLink: '',
        description: '',
        status: 'pending'
    });
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');


    // Get current user from session
    useEffect(() => {
        const getUserData = () => {
            try {
                const userData = sessionStorage.getItem('userData') || localStorage.getItem('userData');
                if (userData) {
                    const parsedData = JSON.parse(userData);
                    console.log('User data loaded:', parsedData);
                    return parsedData;
                }
                
                // Check individual items
                const userRole = sessionStorage.getItem('userRole');
                const userName = sessionStorage.getItem('userName');
                const userId = sessionStorage.getItem('userId');
                
                if (userRole && userName && userId) {
                    return {
                        id: userId,
                        name: userName,
                        role: userRole
                    };
                }
                
                return null;
            } catch (error) {
                console.error('Error loading user data:', error);
                return null;
            }
        };

        const user = getUserData();
        if (user) {
            setCurrentUser(user);
            
            if (user.role === 'designer') {
                setSelectedDesigner(user.id.toString());
            }
            
            if (user.role === 'admin' || user.role === 'manager') {
                fetchDesigners();
            }
        }
    }, []);

    // Fetch designers
    const fetchDesigners = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/get_designers.php`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setDesigners(result.data || []);
            } else {
                setError(result.message || 'Failed to load designers');
            }
        } catch (error) {
            console.error('Error fetching designers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDesignerSelect = (e) => {
        setSelectedDesigner(e.target.value);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!currentUser) {
            setError('Please login first');
            return;
        }

        let designerId = '';
        if (currentUser.role === 'designer') {
            designerId = currentUser.id;
        } else if (currentUser.role === 'admin' || currentUser.role === 'manager') {
            if (!selectedDesigner) {
                setError('Please select a designer');
                return;
            }
            designerId = selectedDesigner;
        }

        if (!formData.videoName.trim()) {
            setError('Video Name is required');
            return;
        }

        try {
            setSubmitting(true);
            setError('');

            const taskData = {
                designerId: designerId,
                videoName: formData.videoName,
                rawLink: formData.rawLink.trim() || null, // Send null if empty
                description: formData.description.trim() || null,
                status: formData.status,
                userRole: currentUser.role,
                userId: currentUser.id,
                userName: currentUser.name
            };

            const response = await fetch(`${API_BASE}/add_designer_task.php`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(taskData)
            });

            const result = await response.json();

            if (result.success) {
                setSuccess(result.message);
                setFormData({
                    videoName: '',
                    rawLink: '',
                    description: '',
                    status: 'pending'
                });
                
                if (currentUser.role === 'admin' || currentUser.role === 'manager') {
                    setSelectedDesigner('');
                }
            } else {
                setError(result.message || 'Failed to save task');
            }
        } catch (error) {
            console.error('Submission error:', error);
            setError('Server connection failed');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            videoName: '',
            rawLink: '',
            description: '',
            status: 'pending'
        });
        
        if (currentUser?.role === 'designer') {
            setSelectedDesigner(currentUser.id.toString());
        } else {
            setSelectedDesigner('');
        }
        
        setError('');
        setSuccess('');
    };

    const isDesigner = currentUser?.role === 'designer';
    const isAdminOrManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';

    return (
        <Layout>
            <div style={styles.container}>
                {/* Animated Background */}
                <div style={styles.animatedBackground}>
                    <div style={{...styles.floatingOrb, ...styles.orb1}}></div>
                    <div style={{...styles.floatingOrb, ...styles.orb2}}></div>
                    <div style={{...styles.floatingOrb, ...styles.orb3}}></div>
                    <div style={{...styles.floatingOrb, ...styles.orb4}}></div>
                    <div style={styles.gridOverlay}></div>
                </div>

                {/* Header Section */}
                <div style={styles.header}>
                    <div style={styles.headerContent}>
                        <div style={styles.greeting}>
                            <h1 style={styles.pageTitle}>
                                <i className="fas fa-palette me-2"></i>
                                Designer Tasks Management
                            </h1>
                            <div style={styles.welcomeBadge}>
                                <span style={styles.badgeText}>Task Assignment</span>
                                <span style={styles.badgeRole}>
                                    {currentUser?.role?.toUpperCase() || 'DESIGNER'}
                                </span>
                            </div>
                        </div>
                        <p style={styles.pageSubtitle}>
                            Assign video editing tasks to designers with detailed requirements
                        </p>
                    </div>
                </div>

                {/* Success/Error Messages */}
                {success && (
                    <div style={styles.successAlert}>
                        <div style={{...styles.alertIcon, color: colors.success}}>
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div style={styles.alertMessage}>
                            <strong>Success</strong>
                            <span>{success}</span>
                        </div>
                        <button style={{...styles.alertClose, color: colors.success}} onClick={() => setSuccess('')}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}
                
                {error && (
                    <div style={styles.errorAlert}>
                        <div style={styles.alertIcon}>
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div style={styles.alertMessage}>
                            <strong>Attention Needed</strong>
                            <span>{error}</span>
                        </div>
                        <button style={styles.alertClose} onClick={() => setError('')}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}

                {/* Main Content - Full Width Form */}
                <div style={styles.content}>
                    <div style={styles.formSectionFull}>
                        <div style={styles.formCard}>
                            <div style={styles.formCardHeader}>
                                <h3 style={styles.formCardTitle}>
                                    <i className="fas fa-plus-circle me-2"></i>
                                    Assign New Designer Task
                                </h3>
                                <p style={styles.formCardSubtitle}>
                                    Fill all required fields to assign a video editing task to a designer
                                </p>
                            </div>
                            <div style={styles.formCardBody}>
                                <form onSubmit={handleSubmit} style={styles.taskForm}>
                                    <div style={styles.formGrid}>
                                        {/* Designer Selection */}
                                        <div style={styles.formGroup}>
                                            <label style={styles.formLabel}>
                                                <i className="fas fa-user me-2"></i>
                                                Select Designer
                                                <span style={styles.required}>*</span>
                                            </label>
                                            
                                            {isDesigner ? (
                                                // Auto-filled for designers
                                                <div style={styles.autoFillDisplay}>
                                                    <div style={styles.designerInfo}>
                                                        <div style={styles.designerAvatar}>
                                                            <i className="fas fa-user-circle"></i>
                                                        </div>
                                                        <div>
                                                            <div style={styles.designerName}>
                                                                {currentUser?.name || 'Designer'}
                                                            </div>
                                                            <div style={styles.designerDetails}>
                                                                <span style={styles.designerRole}>
                                                                    Auto-assigned to you
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                // Dropdown for admin/manager
                                                <select
                                                    style={styles.formSelect}
                                                    value={selectedDesigner}
                                                    onChange={handleDesignerSelect}
                                                    required
                                                    disabled={loading}
                                                >
                                                    <option value="">Choose a designer...</option>
                                                    {designers.map(designer => (
                                                        <option key={designer.id} value={designer.id} style={styles.option}>
                                                            {designer.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            {loading && (
                                                <div style={styles.formHelp}>
                                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                                    Loading designers...
                                                </div>
                                            )}
                                            <div style={styles.formHelp}>
                                                <i className="fas fa-info-circle me-2"></i>
                                                {isDesigner 
                                                    ? 'This task will be automatically assigned to you'
                                                    : 'Select a designer from the list'}
                                            </div>
                                        </div>

                                        {/* Video Name */}
                                        <div style={styles.formGroup}>
                                            <label style={styles.formLabel}>
                                                <i className="fas fa-video me-2"></i>
                                                Task Name
                                                <span style={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                style={styles.formInput}
                                                name="videoName"
                                                value={formData.videoName}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Enter video project name"
                                            />
                                        </div>

                                        {/* Status */}
                                        <div style={styles.formGroup}>
                                            <label style={styles.formLabel}>
                                                <i className="fas fa-flag me-2"></i>
                                                Initial Status
                                            </label>
                                            <select
                                                style={styles.formSelect}
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                            >
                                                <option value="pending" style={styles.option}>Pending</option>
                                                <option value="progress" style={styles.option}>In Progress</option>
                                                <option value="todo" style={styles.option}>To Do</option>
                                                <option value="completed" style={styles.option}>Completed</option>
                                                <option value="approved" style={styles.option}>Approved</option>
                                                <option value="needs_revision" style={styles.option}>Needs Revision</option>
                                            </select>
                                        </div>

                                        {/* Raw Video Link */}
                                        <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                                            <label style={styles.formLabel}>
                                                <i className="fas fa-link me-2"></i>
                                                Raw Video Link
                                                <span style={styles.optional}>(Optional)</span>
                                            </label>
                                            <input
                                                type="url"
                                                style={styles.formInput}
                                                name="rawLink"
                                                value={formData.rawLink}
                                                onChange={handleInputChange}
                                                placeholder="https://dl.live.google.com/raw-video-link (optional)"
                                            />
                                            <div style={styles.formHelp}>
                                                <i className="fas fa-info-circle me-2"></i>
                                                Provide the direct link to raw video files (Google Drive, Dropbox, etc.)
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                                            <label style={styles.formLabel}>
                                                <i className="fas fa-file-alt me-2"></i>
                                                Task Description & Requirements
                                            </label>
                                            <textarea
                                                style={{...styles.formInput, minHeight: '150px'}}
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Describe the editing requirements, special instructions, deadlines, style preferences, etc. (Optional)"
                                            ></textarea>
                                            <div style={styles.formHelp}>
                                                <i className="fas fa-lightbulb me-2"></i>
                                                Be specific about editing requirements, duration, format, and any special effects needed
                                            </div>
                                        </div>
                                    </div>

                                    {/* Form Actions */}
                                    <div style={styles.formActions}>
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            disabled={submitting}
                                            style={styles.resetBtn}
                                        >
                                            <i className="fas fa-undo me-2"></i>
                                            Reset Form
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={submitting || (isAdminOrManager && !selectedDesigner)}
                                            style={{
                                                ...styles.submitBtn,
                                                opacity: (submitting || (isAdminOrManager && !selectedDesigner)) ? 0.6 : 1,
                                                cursor: (submitting || (isAdminOrManager && !selectedDesigner)) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {submitting ? (
                                                <>
                                                    <span style={styles.spinner}></span>
                                                    {isDesigner ? 'Creating Task...' : 'Assigning Task...'}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane me-2"></i>
                                                    {isDesigner ? 'Create Task' : 'Assign Task to Designer'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

/* --- STYLES OBJECT --- */
const styles = {
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.designerGradient,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        maxWidth: '1200px',
        margin: '0 auto'
    },
    greeting: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '15px'
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #fff 0%, #E91E63 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: 0
    },
    welcomeBadge: {
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '8px 16px',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    badgeText: {
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    badgeRole: {
        background: colors.designerGradient,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    pageSubtitle: {
        color: colors.textLight,
        fontSize: '1.2rem',
        margin: 0,
        maxWidth: '600px'
    },
    errorAlert: {
        background: `rgba(${parseInt(colors.danger.slice(1, 3), 16)}, ${parseInt(colors.danger.slice(3, 5), 16)}, ${parseInt(colors.danger.slice(5, 7), 16)}, 0.1)`,
        color: colors.danger,
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${colors.danger}80`,
        maxWidth: '1200px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    successAlert: {
        background: `rgba(${parseInt(colors.success.slice(1, 3), 16)}, ${parseInt(colors.success.slice(3, 5), 16)}, ${parseInt(colors.success.slice(5, 7), 16)}, 0.1)`,
        color: colors.success,
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${colors.success}`,
        maxWidth: '1200px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    alertIcon: {
        fontSize: '1.5rem',
        color: colors.danger
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        color: 'white'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: colors.danger,
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    formSectionFull: {
        marginBottom: '40px'
    },
    formCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '25px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
    },
    formCardHeader: {
        background: colors.designerGradient,
        color: 'white',
        padding: '30px 40px'
    },
    formCardTitle: {
        fontSize: '1.8rem',
        fontWeight: '700',
        margin: '0 0 8px 0'
    },
    formCardSubtitle: {
        color: 'rgba(255, 255, 255, 0.9)',
        margin: 0,
        fontSize: '1.1rem'
    },
    formCardBody: {
        padding: '40px'
    },
    taskForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '30px'
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '25px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    formLabel: {
        color: 'white',
        fontWeight: '600',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center'
    },
    required: {
        color: colors.danger,
        marginLeft: '4px'
    },
    optional: {
        color: colors.accent,
        marginLeft: '4px',
        fontSize: '0.9rem',
        fontStyle: 'italic'
    },
    autoFillDisplay: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '20px',
        color: 'white',
        fontSize: '1rem',
        backdropFilter: 'blur(10px)'
    },
    designerInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    designerAvatar: {
        fontSize: '2.5rem',
        color: colors.primary
    },
    designerName: {
        fontWeight: '600',
        fontSize: '1.1rem',
        color: 'white'
    },
    designerDetails: {
        display: 'flex',
        gap: '15px',
        marginTop: '5px',
        fontSize: '0.85rem'
    },
    designerRole: {
        color: colors.accent,
        background: 'rgba(6, 182, 212, 0.1)',
        padding: '3px 10px',
        borderRadius: '4px'
    },
    formInput: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '15px 20px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        boxSizing: 'border-box',
        resize: 'none'
    },
    formSelect: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '15px 20px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        boxSizing: 'border-box',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'><path d='M7 10l5 5 5-5z'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 15px center',
        backgroundSize: '16px'
    },
    option: {
        background: '#1a1a2e',
        color: 'white',
        padding: '12px 20px'
    },
    formHelp: {
        color: colors.textLight,
        fontSize: '0.85rem',
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    formActions: {
        display: 'flex',
        gap: '15px',
        justifyContent: 'flex-end',
        marginTop: '20px',
        paddingTop: '20px',
        borderTop: `1px solid ${colors.borderDark}`
    },
    resetBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '12px 24px',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center'
    },
    submitBtn: {
        background: colors.designerGradient,
        color: 'white',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    spinner: {
        width: '16px',
        height: '16px',
        border: '2px solid transparent',
        borderTop: '2px solid currentColor',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    }
};

export default DesignerTasks;